import React, { useState, useEffect } from 'react';
import { Trophy, Star, Zap, Target, Crown, Gift, Flame, Users, TrendingUp } from 'lucide-react';
import { database } from '../../Config/firebase';
import { ref, onValue, update, push } from '../../Config/matuRtdb';

const GamificationSystem = () => {
  const [userProfile, setUserProfile] = useState({
    level: 1,
    experience: 0,
    coins: 0,
    gems: 0,
    achievements: [],
    badges: [],
    streak: 0,
    rank: 'Bronze'
  });
  
  const [achievements, setAchievements] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [dailyChallenges, setDailyChallenges] = useState([]);
  const [showReward, setShowReward] = useState(false);

  // Sistema de niveles
  const calculateLevel = (exp) => {
    return Math.floor(exp / 1000) + 1;
  };

  const calculateProgress = (exp) => {
    const currentLevel = calculateLevel(exp);
    const expForCurrentLevel = (currentLevel - 1) * 1000;
    const expForNextLevel = currentLevel * 1000;
    return ((exp - expForCurrentLevel) / (expForNextLevel - expForCurrentLevel)) * 100;
  };

  // Logros disponibles
  const availableAchievements = [
    {
      id: 'first_win',
      title: 'Primera Victoria',
      description: 'Gana tu primera apuesta',
      icon: '🏆',
      reward: { coins: 100, gems: 5 },
      condition: (stats) => stats.totalWins >= 1
    },
    {
      id: 'streak_5',
      title: 'Racha de 5',
      description: 'Gana 5 apuestas consecutivas',
      icon: '🔥',
      reward: { coins: 500, gems: 20 },
      condition: (stats) => stats.currentStreak >= 5
    },
    {
      id: 'vip_member',
      title: 'Miembro VIP',
      description: 'Suscríbete al plan VIP',
      icon: '👑',
      reward: { coins: 1000, gems: 50 },
      condition: (stats) => stats.isVIP
    },
    {
      id: 'social_butterfly',
      title: 'Mariposa Social',
      description: 'Publica 10 posts en la red social',
      icon: '🦋',
      reward: { coins: 300, gems: 15 },
      condition: (stats) => stats.postsCount >= 10
    },
    {
      id: 'casino_king',
      title: 'Rey del Casino',
      description: 'Gana 10,000 monedas en el casino',
      icon: '🎰',
      reward: { coins: 2000, gems: 100 },
      condition: (stats) => stats.casinoWinnings >= 10000
    }
  ];

  // Desafíos diarios
  const generateDailyChallenges = () => [
    {
      id: 'daily_bet',
      title: 'Apuesta Diaria',
      description: 'Realiza al menos 3 apuestas hoy',
      progress: 0,
      target: 3,
      reward: { coins: 150, gems: 10 },
      type: 'betting'
    },
    {
      id: 'daily_social',
      title: 'Social Diario',
      description: 'Interactúa con 5 posts hoy',
      progress: 0,
      target: 5,
      reward: { coins: 100, gems: 5 },
      type: 'social'
    },
    {
      id: 'daily_casino',
      title: 'Casino Diario',
      description: 'Juega 5 partidas en el casino',
      progress: 0,
      target: 5,
      reward: { coins: 200, gems: 15 },
      type: 'casino'
    }
  ];

  // Rangos del sistema
  const ranks = [
    { name: 'Bronze', minExp: 0, color: 'text-amber-600' },
    { name: 'Silver', minExp: 5000, color: 'text-gray-400' },
    { name: 'Gold', minExp: 15000, color: 'text-yellow-400' },
    { name: 'Platinum', minExp: 30000, color: 'text-blue-400' },
    { name: 'Diamond', minExp: 50000, color: 'text-purple-400' },
    { name: 'Master', minExp: 100000, color: 'text-red-400' }
  ];

  const getCurrentRank = (exp) => {
    for (let i = ranks.length - 1; i >= 0; i--) {
      if (exp >= ranks[i].minExp) {
        return ranks[i];
      }
    }
    return ranks[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header con perfil del usuario */}
        <div className="bg-gradient-to-r from-purple-800/50 to-blue-800/50 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-purple-600/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-2xl font-bold">
                  {userProfile.level}
                </div>
                <div className="absolute -bottom-1 -right-1 bg-yellow-500 text-black rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                  {getCurrentRank(userProfile.experience).name.charAt(0)}
                </div>
              </div>
              
              <div>
                <h1 className="text-2xl font-bold">Nivel {userProfile.level}</h1>
                <p className={`text-lg font-semibold ${getCurrentRank(userProfile.experience).color}`}>
                  {getCurrentRank(userProfile.experience).name}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm">{userProfile.coins}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-purple-400" />
                    <span className="text-sm">{userProfile.gems}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Flame className="w-4 h-4 text-red-400" />
                    <span className="text-sm">{userProfile.streak} días</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-300 mb-1">Experiencia</div>
              <div className="text-lg font-bold">{userProfile.experience} / {userProfile.level * 1000}</div>
              <div className="w-32 bg-gray-700 rounded-full h-2 mt-2">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${calculateProgress(userProfile.experience)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Desafíos diarios */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-yellow-400" />
            Desafíos Diarios
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dailyChallenges.map((challenge) => (
              <div key={challenge.id} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg">{challenge.title}</h3>
                  <div className="text-sm text-gray-400">
                    {challenge.progress}/{challenge.target}
                  </div>
                </div>
                <p className="text-gray-300 text-sm mb-3">{challenge.description}</p>
                <div className="w-full bg-gray-700 rounded-full h-2 mb-3">
                  <div 
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2 rounded-full transition-all"
                    style={{ width: `${(challenge.progress / challenge.target) * 100}%` }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm">{challenge.reward.coins}</span>
                    <Zap className="w-4 h-4 text-purple-400" />
                    <span className="text-sm">{challenge.reward.gems}</span>
                  </div>
                  <button 
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      challenge.progress >= challenge.target
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-gray-600 cursor-not-allowed'
                    }`}
                    disabled={challenge.progress < challenge.target}
                  >
                    {challenge.progress >= challenge.target ? 'Reclamar' : 'En Progreso'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Logros */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-400" />
            Logros
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableAchievements.map((achievement) => {
              const isCompleted = userProfile.achievements.includes(achievement.id);
              return (
                <div 
                  key={achievement.id} 
                  className={`backdrop-blur-sm rounded-xl p-4 border transition-all ${
                    isCompleted 
                      ? 'bg-green-800/50 border-green-600/50' 
                      : 'bg-gray-800/50 border-gray-700/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`text-2xl ${isCompleted ? 'animate-pulse' : ''}`}>
                      {achievement.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold">{achievement.title}</h3>
                      <p className="text-sm text-gray-300">{achievement.description}</p>
                    </div>
                  </div>
                  
                  {isCompleted ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <Gift className="w-4 h-4" />
                      <span className="text-sm">¡Completado!</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Target className="w-4 h-4" />
                      <span className="text-sm">En progreso...</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Crown className="w-6 h-6 text-yellow-400" />
            Ranking Global
          </h2>
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50">
            <div className="space-y-3">
              {leaderboard.map((player, index) => (
                <div key={player.id} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500 text-black' :
                      index === 1 ? 'bg-gray-400 text-black' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-gray-600 text-white'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex items-center gap-2">
                      <img src={player.avatar} alt={player.name} className="w-8 h-8 rounded-full" />
                      <span className="font-semibold">{player.name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-300">Nivel</div>
                      <div className="font-bold">{player.level}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-300">Exp</div>
                      <div className="font-bold">{player.experience}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recompensas especiales */}
        <div>
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Gift className="w-6 h-6 text-purple-400" />
            Recompensas Especiales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-purple-800/50 to-pink-800/50 backdrop-blur-sm rounded-xl p-6 border border-purple-600/30">
              <div className="flex items-center gap-3 mb-4">
                <Crown className="w-8 h-8 text-yellow-400" />
                <h3 className="text-xl font-bold">VIP Rewards</h3>
              </div>
              <ul className="space-y-2 text-gray-300">
                <li>• 2x experiencia en todas las actividades</li>
                <li>• Acceso exclusivo a eventos especiales</li>
                <li>• Recompensas diarias mejoradas</li>
                <li>• Soporte prioritario 24/7</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-blue-800/50 to-cyan-800/50 backdrop-blur-sm rounded-xl p-6 border border-blue-600/30">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-8 h-8 text-blue-400" />
                <h3 className="text-xl font-bold">Referral Program</h3>
              </div>
              <ul className="space-y-2 text-gray-300">
                <li>• 1000 monedas por cada amigo invitado</li>
                <li>• 50 gemas por cada 5 referidos</li>
                <li>• Bonus especial al llegar a 10 referidos</li>
                <li>• Comisión del 5% de sus ganancias</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamificationSystem; 